import { useEffect, useRef, useState, useCallback } from 'react'
import { useUser } from './use-user'
import { apiFetch } from '@/utils'
import type { Notification, NotificationState } from '@/constants/notifications'

const WS_URL = import.meta.env.VITE_WS_URL
const RECONNECT_DELAY = 3000
const MAX_RECONNECT_ATTEMPTS = 5
const HEARTBEAT_INTERVAL = 30000

interface UseNotificationsReturn extends NotificationState {
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  refetch: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const { uid, id } = useUser()
  const userId = uid || id
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempts = useRef(0)
  const heartbeatInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const fetchNotificationsRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    isLoading: true,
    error: null
  })

  // Fetch notifications from REST API
  const fetchNotifications = useCallback(async () => {
    if (!userId) return

    try {
      const response = await apiFetch('/v1/notifications?limit=50&offset=0', {
        withCredentials: true
      })

      if (!response.ok) throw new Error('Failed to fetch notifications')

      const json = await response.json()
      const rawNotifications = json.data?.notifications ?? json.notifications ?? []
      // Filter out invalid notifications
      const notifications = rawNotifications.filter((n: Notification) => n && n.id && n.createdAt)

      setState(prev => ({
        ...prev,
        notifications,
        unreadCount: notifications.filter((n: Notification) => !n.read).length,
        isLoading: false,
        error: null
      }))
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to load notifications'
      }))
    }
  }, [userId])

  // Keep ref updated so WebSocket handler always calls current version
  fetchNotificationsRef.current = fetchNotifications

  // Fetch unread count (lightweight polling fallback)
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return

    try {
      const response = await apiFetch('/v1/notifications/unread-count', {
        withCredentials: true
      })

      if (!response.ok) return

      const json = await response.json()
      const count = json.data?.count ?? json.count

      setState(prev => ({
        ...prev,
        unreadCount: count,
        isLoading: false
      }))
    } catch (error) {
      console.error('Failed to fetch unread count:', error)
    }
  }, [userId])

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!userId || !WS_URL || wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    try {
      const ws = new WebSocket(`${WS_URL}?userId=${userId}`)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('Notifications WebSocket connected')
        reconnectAttempts.current = 0

        setState(prev => ({
          ...prev,
          isConnected: true,
          error: null
        }))

        // Start heartbeat
        heartbeatInterval.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'PING' }))
          }
        }, HEARTBEAT_INTERVAL)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'NEW_NOTIFICATION':
              // Refetch to get the new notification (use ref to get current function)
              fetchNotificationsRef.current()
              break

            case 'PONG':
              // Heartbeat response, connection is alive
              break
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)

        setState(prev => ({ ...prev, isConnected: false }))

        // Clear heartbeat
        if (heartbeatInterval.current) {
          clearInterval(heartbeatInterval.current)
          heartbeatInterval.current = null
        }

        // Attempt reconnect
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts.current++
          console.log(`Reconnecting... (attempt ${reconnectAttempts.current})`)
          setTimeout(connect, RECONNECT_DELAY)
        } else {
          setState(prev => ({
            ...prev,
            error: 'Connection lost. Please refresh the page.'
          }))
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
    }
  }, [userId])

  // Mark single notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    // Optimistic update
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ),
      unreadCount: Math.max(0, prev.unreadCount - 1)
    }))

    // Always use REST API to persist the change
    try {
      await apiFetch(`/v1/notifications/${notificationId}/read`, {
        method: 'PATCH',
        withCredentials: true
      })
    } catch (error) {
      console.error('Failed to mark as read:', error)
      // Revert optimistic update on failure
      fetchNotifications()
    }
  }, [fetchNotifications])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    // Optimistic update
    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n => ({ ...n, read: true })),
      unreadCount: 0
    }))

    // Always use REST API to persist the change
    try {
      await apiFetch('/v1/notifications/mark-all-read', {
        method: 'POST',
        withCredentials: true
      })
    } catch (error) {
      console.error('Failed to mark all as read:', error)
      fetchNotifications()
    }
  }, [fetchNotifications])

  // Initialize
  useEffect(() => {
    if (!userId) {
      setState({
        notifications: [],
        unreadCount: 0,
        isConnected: false,
        isLoading: false,
        error: null
      })
      return
    }

    // Always fetch unread count on load
    fetchUnreadCount()

    // Try WebSocket for real-time updates
    if (WS_URL) {
      connect()
    } else {
      // Fallback to polling if no WebSocket URL configured
      console.log('WebSocket URL not configured, using polling')
      const pollInterval = setInterval(fetchUnreadCount, 30000)
      return () => clearInterval(pollInterval)
    }

    // Cleanup
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current)
        heartbeatInterval.current = null
      }
    }
  }, [userId, connect, fetchUnreadCount])

  return {
    ...state,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications
  }
}
